import { array, arrayOf, bool, func, object, shape, string } from 'prop-types';
import React, { Component } from 'react';
import { NavLink } from 'react-router-dom';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import styled from 'styled-components';
import { withRouter } from 'react-router';

import { isEmpty } from 'ravenjs/utils/lodash';
import Link from 'ravenjs/lib/Link';

import { DEFAULT_COLORS } from 'constants/colors';
import { EVENTS } from 'constants/events';
import MESSAGES from 'constants/messages';
import {
    HR_COMPLIANCE_MENU_STATES,
    HR_COMPLIANCE_SECTIONS,
    IMPERSONATION_RESTRICTED_SUB_NAV_ITEMS,
} from 'constants/navigation';
import { SAFETY, SAFETY_MANUAL_TEMPLATE_ID } from 'constants/safety';
import Acl from 'modules/acl';
import { selectors as authSelectors } from 'modules/auth';
import { actions as contentfulActions } from 'modules/contentful';
import { actions as pendoActions } from 'modules/pendo';
import { actions as uiActions } from 'modules/ui';
import { selectors as userSelectors } from 'modules/user';
import { selectors as ticketSelectors } from 'modules/tickets';
import { formatSubNavItems, getSubNavToProp } from 'utils/navigation';
import {
    downloadSafetyManualTemplate,
    filterSafetySubnavigation,
    isDisableSafetyItem,
    sendPendoEventToolClick,
} from 'utils/safety';
import { handleVergigChatClick } from 'utils/common';
import { MNL_INTELLIGENCE } from 'constants/intelligence';
import {
    CHAT_TECH_SUPPORT_EVENT,
    CHAT_TECH_SUPPORT_STATE,
    CONTACT_HR_EXPERT_EVENT,
    CONTACT_HR_EXPERT_STATE,
    CONTACT_TECH_SUPPORT_EVENT,
    CONTACT_TECH_SUPPORT_STATE,
    HELP_MENU_TITLE,
    HELP_TOPICS_EVENT,
    HELP_TOPICS_STATE,
} from 'constants/helpCenter';
import { shouldHideExperts } from 'utils/user';

const AppSubNavigationMainStyledCommon = styled.div`
    display: flex;
    flex-direction: column;
    margin: 0;
`;

const AppSubNavigationMainStyledV1 = styled(AppSubNavigationMainStyledCommon)`
    padding: 0 1.1875rem;
`;

const AppSubNavigationMainStyledV2 = styled(AppSubNavigationMainStyledCommon)`
    padding: 0;
    width: 308px;
    min-width: 200px;
    flex: 1 1 308px;
`;

const LinkStyledCommon = styled(Link)`
    color: ${DEFAULT_COLORS.BLACK} !important;
    text-decoration: none;
`;

const LinkStyledV1 = styled(LinkStyledCommon)`
    :hover {
        color: ${DEFAULT_COLORS.LINK} !important;
        text-decoration: none;
    }
`;

const MenuItemDescription = styled.span`
    font-family: Roboto, serif;
    font-size: 14px;
    line-height: 150%;
    color: ${DEFAULT_COLORS.GREY_27};
    font-weight: 400;
    vertical-align: middle;
    overflow-wrap: break-word;
    white-space: normal;
    max-width: 100%;
    word-wrap: break-word;
`;

const LinkStyledV2 = styled(LinkStyledCommon)`
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: 4px;
    border-radius: 4px;
    padding: 8px 12px;
    box-sizing: border-box;
    &:hover {
        background: ${DEFAULT_COLORS.BLUE_7};
        color: ${DEFAULT_COLORS.BLACK} !important;
        text-decoration: none;
    }
    &:hover ${MenuItemDescription} {
        color: ${DEFAULT_COLORS.BLACK};
    }
    &:focus {
        outline: 2px solid ${DEFAULT_COLORS.LINK};
        outline-offset: 2px;
    }
    &:focus:not(:focus-visible) {
        outline: none;
    }
    &:focus-visible {
        outline: 2px solid ${DEFAULT_COLORS.LINK};
        outline-offset: 2px;
    }
`;

const NavLinkStyledCommon = styled(NavLink)`
    color: ${DEFAULT_COLORS.BLACK};
    & {
        cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
    }
`;

const NavLinkStyledV1 = styled(NavLinkStyledCommon)`
    :hover {
        color: ${DEFAULT_COLORS.LINK};
        text-decoration: none;
    }
`;

const NavLinkStyledV2 = styled(NavLinkStyledCommon)`
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: 4px;
    border-radius: 4px;
    padding: 8px 12px;
    box-sizing: border-box;
    &:hover {
        background: ${({ disabled }) => (disabled ? 'transparent' : `${DEFAULT_COLORS.BLUE_7}`)};
        color: ${DEFAULT_COLORS.BLACK};
        text-decoration: none;
    }
    &:hover ${MenuItemDescription} {
        color: ${DEFAULT_COLORS.BLACK};
    }
    &:focus {
        outline: 2px solid ${DEFAULT_COLORS.LINK};
        outline-offset: 2px;
    }
    &:focus:not(:focus-visible) {
        outline: none;
    }
    &:focus-visible {
        outline: 2px solid ${DEFAULT_COLORS.LINK};
        outline-offset: 2px;
    }
`;

const MenuItemLabel = styled.span`
    font-weight: 400;
    font-family: Roboto, serif;
    font-size: 16px;
    line-height: 150%;
    color: ${DEFAULT_COLORS.BLACK};
    vertical-align: middle;
`;

const ComplianceSectionHeaderV2 = styled.div`
    font-family: 'Favorit';
    font-weight: 400;
    font-size: 18px;
    font-style: normal;
    color: ${DEFAULT_COLORS.GREY_27};
    letter-spacing: 0;
    padding: 12px;
    margin: 0;
`;

const ComplianceColumnsWrapperV2 = styled.div`
    display: flex;
    flex-direction: row;
    gap: 24px;
    align-items: flex-start;
`;

const linkStyle = {
    fontWeight: 'normal',
    fontFamily: 'Roboto',
    fontSize: '1rem',
    marginBlockStart: '.1875rem',
    marginBlockEnd: '.1875rem',
    marginInlineStart: '0',
    marginInlineEnd: '0',
};

const linkStyleV2 = {
    fontWeight: 'normal',
    fontFamily: 'Roboto',
    fontSize: '1rem',
};

class AppSubNavigationMain extends Component {
    static propTypes = {
        closeModal: func.isRequired,
        currentUser: object.isRequired,
        fetchSafetyManualTemplate: func.isRequired,
        isImpersonatingUser: bool,
        isLoading: func.isRequired,
        items: array,
        mainState: string,
        openModal: func.isRequired,
        safetyManualTemplateUrl: string,
        sendPendoEvent: func.isRequired,
        userCompany: object.isRequired,
        userInfo: object.isRequired,
        userPermissionsOriginal: arrayOf(
            shape({
                permissionKey: string,
            })
        ),
        history: object.isRequired,
        getZendeskSubmitUrl: string.isRequired,
        isNavigationV2Enabled: bool,
        onSubmenuItemClick: func,
    };

    static defaultProps = {
        isImpersonatingUser: false,
        items: [],
        mainState: '',
        safetyManualTemplateUrl: '',
        userPermissionsOriginal: [],
        isNavigationV2Enabled: false,
        onSubmenuItemClick: undefined,
    };

    refCallback = node => {
        const isEmployeeHandbooks = node && node.href.includes('handbooks-express');
        if (isEmployeeHandbooks) {
            node.onclick = () => {
                if (window.location.href.includes('/company-policies/handbooks-express')) {
                    window.location.reload();
                }
            };
        }
    };

    onClickNav = async (mainState, item) => {
        const { sendPendoEvent, currentUser, userCompany, onSubmenuItemClick } = this.props;
        const pendoEvent = EVENTS.NAVIGATION.SELECT;

        pendoEvent.eventProperties.selection = `${mainState}:${item.state}`;
        sendPendoEvent(pendoEvent);

        if (mainState === MNL_INTELLIGENCE.MINTEL_NAV_STATE) {
            if (item.state === MNL_INTELLIGENCE.MINTEL_NAV_STATE) {
                const intelligencePendoEvent = EVENTS.INTELLIGENCE.REDIRECT_TO_DASHBOARD;
                intelligencePendoEvent.eventProperties.source = 'HR Compliance Dashboard';
                intelligencePendoEvent.eventProperties.userName = currentUser.userName;
                intelligencePendoEvent.eventProperties.companyId = userCompany.companyId;
                intelligencePendoEvent.eventProperties.companyName = userCompany.companyName;
                sendPendoEvent(intelligencePendoEvent);
            } else if (item.state === 'myinsights') {
                const intelligencePendoEvent = EVENTS.INTELLIGENCE.REDIRECT_TO_INSIGHTS;
                intelligencePendoEvent.eventProperties.source = 'HR Compliance Insights';
                intelligencePendoEvent.eventProperties.userName = currentUser.userName;
                intelligencePendoEvent.eventProperties.companyId = userCompany.companyId;
                intelligencePendoEvent.eventProperties.companyName = userCompany.companyName;
                sendPendoEvent(intelligencePendoEvent);
            }
        } else if (mainState.toLowerCase() === String(HELP_MENU_TITLE).toLowerCase()) {
            const helpCenterPendoEvent = EVENTS.NAVIGATION.HELP_CENTER;
            helpCenterPendoEvent.eventProperties.source = item.state;
            helpCenterPendoEvent.eventProperties.userName = currentUser.userName;
            helpCenterPendoEvent.eventProperties.companyId = userCompany.companyId;
            helpCenterPendoEvent.eventProperties.companyName = userCompany.companyName;
            switch (item.state) {
                case HELP_TOPICS_STATE:
                    helpCenterPendoEvent.event = HELP_TOPICS_EVENT;
                    break;
                case CONTACT_TECH_SUPPORT_STATE:
                    helpCenterPendoEvent.event = CONTACT_TECH_SUPPORT_EVENT;
                    break;
                case CHAT_TECH_SUPPORT_STATE:
                    helpCenterPendoEvent.event = CHAT_TECH_SUPPORT_EVENT;
                    break;
                case CONTACT_HR_EXPERT_STATE:
                    helpCenterPendoEvent.event = CONTACT_HR_EXPERT_EVENT;
                    break;
                default:
                    break;
            }
            sendPendoEvent(helpCenterPendoEvent);
        }
        if (mainState === SAFETY.toLowerCase()) {
            this.sendEventToolClick(item.label);
        }

        if (item.id === 'vergigchat') {
            await handleVergigChatClick();
        }

        // Close the submenu after clicking any item
        if (onSubmenuItemClick) {
            onSubmenuItemClick();
        }
    };

    sendEventToolClick = toolName => {
        const { currentUser, sendPendoEvent, userCompany, userPermissionsOriginal } = this.props;

        sendPendoEventToolClick({
            currentUser,
            from: 'Navigation',
            sendPendoEvent,
            toolName,
            userCompany,
            userPermissionsOriginal,
        });
    };

    filterUnauthorized = item => {
        if (item.permissions && item.permissionToHide) {
            return Acl.check(item.permissions) && !Acl.check(item.permissionToHide);
        } else if (item.permissionToHide) {
            return !Acl.check(item.permissionToHide);
        } else if (item.permission) {
            return Acl.check(item.permission);
        }
        return filterSafetySubnavigation(item);
    };

    renderComplianceSectionColumn = (formattedItems, mainState, sectionKey, pendoData) => {
        const { isImpersonatingUser } = this.props;

        const sectionConfig = HR_COMPLIANCE_SECTIONS[sectionKey];

        const sectionItems = sectionConfig.itemStates
            .map(state => formattedItems.find(item => item.state === state))
            .filter(Boolean)
            .filter(this.filterUnauthorized);

        if (sectionItems.length === 0) {
            return null;
        }

        const renderLabel = (item, descriptionId) => (
            <>
                <MenuItemLabel>{item.label}</MenuItemLabel>
                {item.description && (
                    <MenuItemDescription id={descriptionId} aria-hidden="true">
                        {item.description}
                    </MenuItemDescription>
                )}
            </>
        );

        return (
            <AppSubNavigationMainStyledV2 role="group" aria-label={sectionConfig.title}>
                <ComplianceSectionHeaderV2 role="presentation" aria-hidden="true">
                    {sectionConfig.title}
                </ComplianceSectionHeaderV2>
                {sectionItems.map(item => {
                    const navLinkDisabled =
                        (isImpersonatingUser &&
                            IMPERSONATION_RESTRICTED_SUB_NAV_ITEMS.includes(item.to)) ||
                        isDisableSafetyItem(item.state);

                    const navLinkStyledProps = navLinkDisabled
                        ? {
                              disabled: true,
                              onClick: e => {
                                  e.preventDefault();
                              },
                              title: MESSAGES.RESTRICTED_NAV_ITEM,
                          }
                        : {
                              onClick: () => this.onClickNav(mainState, item),
                          };

                    return (
                        <NavLinkStyledV2
                            to={getSubNavToProp(mainState, item)}
                            innerRef={this.refCallback}
                            style={linkStyleV2}
                            key={item.state}
                            data-submenu-item="true"
                            tabIndex={0}
                            role="menuitem"
                            aria-describedby={
                                item.description ? `${mainState}-${item.state}-desc` : undefined
                            }
                            {...navLinkStyledProps}
                        >
                            {renderLabel(item, `${mainState}-${item.state}-desc`)}
                        </NavLinkStyledV2>
                    );
                })}
            </AppSubNavigationMainStyledV2>
        );
    };

    renderItems = (items, mainState) => {
        const {
            closeModal,
            currentUser,
            fetchSafetyManualTemplate,
            isImpersonatingUser,
            isLoading,
            openModal,
            safetyManualTemplateUrl,
            sendPendoEvent,
            userCompany,
            userInfo,
            userPermissionsOriginal,
            getZendeskSubmitUrl,
            isNavigationV2Enabled,
        } = this.props;

        const zendeskSubmitURL = getZendeskSubmitUrl;

        const formattedItems = formatSubNavItems(
            items,
            mainState,
            zendeskSubmitURL,
            shouldHideExperts(userInfo, userCompany)
        );

        const pendoData = {
            currentUser,
            sendPendoEvent,
            userCompany,
            userPermissionsOriginal,
        };

        if (isNavigationV2Enabled && HR_COMPLIANCE_MENU_STATES.includes(mainState)) {
            return (
                <ComplianceColumnsWrapperV2>
                    {this.renderComplianceSectionColumn(
                        formattedItems,
                        mainState,
                        'PERSONALIZED_COMPLIANCE',
                        pendoData
                    )}
                    {this.renderComplianceSectionColumn(
                        formattedItems,
                        mainState,
                        'COMPLIANCE_EXPERTISE',
                        pendoData
                    )}
                </ComplianceColumnsWrapperV2>
            );
        }

        const LinkComponent = isNavigationV2Enabled ? LinkStyledV2 : LinkStyledV1;
        const NavLinkComponent = isNavigationV2Enabled ? NavLinkStyledV2 : NavLinkStyledV1;
        const currentLinkStyle = isNavigationV2Enabled ? linkStyleV2 : linkStyle;

        const renderLabel = (item, descriptionId) => {
            if (isNavigationV2Enabled) {
                return (
                    <>
                        <MenuItemLabel>{item.label}</MenuItemLabel>
                        {item.description && (
                            <MenuItemDescription id={descriptionId} aria-hidden="true">
                                {item.description}
                            </MenuItemDescription>
                        )}
                    </>
                );
            }
            return item.label;
        };

        return (
            <>
                {formattedItems.filter(this.filterUnauthorized).map(item => {
                    const navLinkDisabled =
                        (isImpersonatingUser &&
                            IMPERSONATION_RESTRICTED_SUB_NAV_ITEMS.includes(item.to)) ||
                        isDisableSafetyItem(item.state);

                    const navLinkStyledProps = navLinkDisabled
                        ? {
                              disabled: true,
                              onClick: e => {
                                  e.preventDefault();
                              },
                              title: MESSAGES.RESTRICTED_NAV_ITEM,
                          }
                        : {
                              onClick: () => this.onClickNav(mainState, item),
                          };

                    if (
                        (mainState === 'training' || mainState === 'hrtools') &&
                        item.to.indexOf('https') !== -1
                    ) {
                        const externalLinkStyle = navLinkDisabled
                            ? { ...linkStyle, cursor: 'not-allowed' }
                            : linkStyle;

                        return (
                            <LinkComponent
                                style={externalLinkStyle}
                                key={item.state}
                                href={navLinkDisabled ? '#' : item.to}
                                target="_blank"
                                onClick={() => this.onClickNav(mainState, item)}
                                // Added data attribute for keyboard navigation
                                data-submenu-item="true"
                                // Added tabIndex for keyboard focus
                                tabIndex={isNavigationV2Enabled ? 0 : undefined}
                                role={isNavigationV2Enabled ? 'menuitem' : undefined}
                                aria-describedby={
                                    isNavigationV2Enabled && item.description
                                        ? `${mainState}-${item.state}-desc`
                                        : undefined
                                }
                                {...navLinkStyledProps}
                            >
                                {renderLabel(item, `${mainState}-${item.state}-desc`)}
                            </LinkComponent>
                        );
                    }

                    if (item.to === SAFETY_MANUAL_TEMPLATE_ID) {
                        const navLinkStyledPropsManual = navLinkDisabled
                            ? {
                                  disabled: true,
                                  onClick: e => {
                                      e.preventDefault();
                                  },
                                  title: MESSAGES.RESTRICTED_NAV_ITEM,
                              }
                            : {
                                  onClick: downloadSafetyManualTemplate({
                                      closeModal,
                                      fetchSafetyManualTemplate,
                                      isLoading,
                                      openModal,
                                      safetyManualTemplateUrl,
                                      sendEvent: this.sendEventToolClick,
                                      pendoData,
                                      product: { title: item.label },
                                  }),
                              };

                        const linkStyleManual = navLinkDisabled
                            ? { ...linkStyle, cursor: 'not-allowed' }
                            : linkStyle;
                        return (
                            <LinkComponent
                                style={linkStyleManual}
                                key={item.state}
                                href="#"
                                // Added data attribute for keyboard navigation
                                data-submenu-item="true"
                                // Added tabIndex for keyboard focus
                                tabIndex={isNavigationV2Enabled ? 0 : undefined}
                                role={isNavigationV2Enabled ? 'menuitem' : undefined}
                                aria-describedby={
                                    isNavigationV2Enabled && item.description
                                        ? `${mainState}-${item.state}-desc`
                                        : undefined
                                }
                                {...navLinkStyledPropsManual}
                            >
                                {renderLabel(item, `${mainState}-${item.state}-desc`)}
                            </LinkComponent>
                        );
                    }

                    if (item.state === 'contacttechnicalsupport') {
                        return (
                            <LinkComponent
                                style={currentLinkStyle}
                                key={item.state}
                                href={item.to}
                                target="_blank"
                                rel="noopener noreferrer"
                                // Added data attribute for keyboard navigation
                                data-submenu-item="true"
                                // Added tabIndex for keyboard focus
                                tabIndex={isNavigationV2Enabled ? 0 : undefined}
                                role={isNavigationV2Enabled ? 'menuitem' : undefined}
                                aria-describedby={
                                    isNavigationV2Enabled && item.description
                                        ? `${mainState}-${item.state}-desc`
                                        : undefined
                                }
                                {...navLinkStyledProps}
                            >
                                {renderLabel(item, `${mainState}-${item.state}-desc`)}
                            </LinkComponent>
                        );
                    }

                    return (
                        <NavLinkComponent
                            to={getSubNavToProp(mainState, item)}
                            innerRef={this.refCallback}
                            style={currentLinkStyle}
                            key={item.state}
                            // Added data attribute for keyboard navigation
                            data-submenu-item="true"
                            // Added tabIndex for keyboard focus
                            tabIndex={isNavigationV2Enabled ? 0 : undefined}
                            role={isNavigationV2Enabled ? 'menuitem' : undefined}
                            aria-describedby={
                                isNavigationV2Enabled && item.description
                                    ? `${mainState}-${item.state}-desc`
                                    : undefined
                            }
                            {...navLinkStyledProps}
                        >
                            {renderLabel(item, `${mainState}-${item.state}-desc`)}
                        </NavLinkComponent>
                    );
                })}
            </>
        );
    };

    render() {
        const { items, mainState, isNavigationV2Enabled } = this.props;

        if (!isEmpty(items) && items.length > 0) {
            if (isNavigationV2Enabled && HR_COMPLIANCE_MENU_STATES.includes(mainState)) {
                return this.renderItems(items, mainState);
            }

            const Container = isNavigationV2Enabled
                ? AppSubNavigationMainStyledV2
                : AppSubNavigationMainStyledV1;
            return <Container>{this.renderItems(items, mainState)}</Container>;
        }

        return null;
    }
}

const mapDispatchToProps = {
    closeModal: uiActions.closeModal,
    fetchSafetyManualTemplate: contentfulActions.fetchSafetyManualTemplate,
    isLoading: uiActions.isLoading,
    openModal: uiActions.openModal,
    sendPendoEvent: pendoActions.sendPendoEvent,
};

const mapStateToProps = createStructuredSelector({
    currentUser: userSelectors.getUserInfo,
    getZendeskSubmitUrl: ticketSelectors.getZendeskSubmitUrl,
    isImpersonatingUser: authSelectors.isImpersonatingUser,
    safetyManualTemplateUrl: userSelectors.getSafetyManualTemplateUrl,
    userCompany: userSelectors.getUserCompany,
    userInfo: userSelectors.getUserInfo,
    userPermissionsOriginal: userSelectors.getUserPermissionsOriginal,
});

export { AppSubNavigationMain as AppSubNavigationMainUnwrapped };
export default withRouter(connect(mapStateToProps, mapDispatchToProps)(AppSubNavigationMain));
